/**
 * OrchestrationConnection - WebSocket service for real-time agent orchestration
 * Manages connections to orchestration engine with automatic reconnection
 * Implements Epic 3 real-time integration patterns
 */

export type AgentStatus = 'idle' | 'executing' | 'waiting' | 'error';

export interface AgentActivity {
  agentId: string;
  agentType: string;
  status: AgentStatus;
  currentTask?: string;
  progress?: number;
  dependencies?: string[];
  outputs?: Record<string, unknown>[];
  executionTime?: number;
  lastUpdated: number;
}

export interface WorkflowExecution {
  workflowId: string;
  name: string;
  status: 'running' | 'paused' | 'completed' | 'failed';
  progress: number;
  agents: string[];
  startTime: number;
  estimatedCompletion?: number;
}

export interface AgentCommand {
  agentId: string;
  command: string;
  parameters?: Record<string, any>;
}

export interface CommandResult {
  success: boolean;
  message: string;
  data?: Record<string, unknown>;
}

export interface WorkflowDefinition {
  name: string;
  agents: string[];
  dependencies: Record<string, string[]>;
  parameters?: Record<string, unknown>;
}

class OrchestrationConnectionClass {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000; // Start with 1 second
  private isReconnecting = false;
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private wsUrl: string;
  private updateBatch: {type?: string; payload?: unknown; timestamp?: number}[] = [];
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_DELAY = 50; // 50ms batching for <200ms performance
  private readonly MAX_BATCH_SIZE = 20;
  private readonly LATENCY_WARNING_THRESHOLD = 200; // 200ms warning threshold
  private readonly LATENCY_CRITICAL_THRESHOLD = 500; // 500ms critical threshold
  private performanceMetrics = {
    lastMessageTime: 0,
    averageLatency: 0,
    messageCount: 0,
    connectionUptime: 0,
    connectionStartTime: 0,
    latencyWarnings: 0,
    latencyCritical: 0
  };

  constructor() {
    // Use environment variable or default to local development
    this.wsUrl = import.meta.env.VITE_ORCHESTRATION_WS_URL || 'ws://localhost:8002/ws/orchestration';  // E9: Corrected from 8001 to 8002
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      try {
        this.ws = new WebSocket(this.wsUrl);

        this.ws.onopen = () => {
          console.log('OrchestrationConnection: Connected to orchestration engine');
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          this.isReconnecting = false;
          this.performanceMetrics.connectionStartTime = Date.now();
          this.emit('connected', true);
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const receiveTime = Date.now();
            const data = JSON.parse(event.data);
            
            // Update performance metrics
            this.updatePerformanceMetrics(receiveTime, data);
            
            // Add to batch for processing
            this.addToBatch(data);
          } catch (error) {
            console.error('OrchestrationConnection: Failed to parse message', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('OrchestrationConnection: Disconnected', event.code, event.reason);
          this.emit('connected', false);
          
          if (!this.isReconnecting && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.attemptReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('OrchestrationConnection: WebSocket error', error);
          this.emit('error', error);
          reject(error);
        };

      } catch (error) {
        console.error('OrchestrationConnection: Failed to create WebSocket', error);
        reject(error);
      }
    });
  }

  private attemptReconnect(): void {
    if (this.isReconnecting) return;
    
    this.isReconnecting = true;
    this.reconnectAttempts++;

    console.log(`OrchestrationConnection: Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

    setTimeout(() => {
      this.connect().catch(() => {
        // Exponential backoff with jitter
        this.reconnectDelay = Math.min(this.reconnectDelay * 2 + Math.random() * 1000, 30000);
        this.isReconnecting = false;
      });
    }, this.reconnectDelay);
  }

  private handleMessage(data: {type?: string; payload?: unknown | unknown[]}): void {
    switch (data.type) {
      case 'agent_activity':
        this.emit('agentActivity', data.payload);
        break;
      case 'workflow_progress':
        this.emit('workflowProgress', data.payload);
        break;
      case 'agent_status_update':
        this.emit('agentStatusUpdate', data.payload);
        break;
      case 'system_alert':
        this.emit('systemAlert', data.payload);
        break;
      case 'batch_update':
        // Handle batched updates for performance
        (data.payload as unknown[]).forEach((update: unknown) => this.handleMessage(update as {type?: string; payload?: unknown}));
        break;
      default:
        console.warn('OrchestrationConnection: Unknown message type', data.type);
    }
  }

  private updatePerformanceMetrics(receiveTime: number, data: {timestamp?: number}): void {
    this.performanceMetrics.messageCount++;
    this.performanceMetrics.lastMessageTime = receiveTime;
    
    // Calculate latency if timestamp is provided
    if (data.timestamp) {
      const latency = receiveTime - data.timestamp;
      this.performanceMetrics.averageLatency = 
        (this.performanceMetrics.averageLatency * (this.performanceMetrics.messageCount - 1) + latency) / 
        this.performanceMetrics.messageCount;
      
      // Check latency thresholds and emit alerts
      if (latency > this.LATENCY_CRITICAL_THRESHOLD) {
        this.performanceMetrics.latencyCritical++;
        console.warn(`OrchestrationConnection: Critical latency detected: ${latency}ms (threshold: ${this.LATENCY_CRITICAL_THRESHOLD}ms)`);
        this.emit('performanceAlert', {
          type: 'latency_critical',
          latency,
          threshold: this.LATENCY_CRITICAL_THRESHOLD,
          timestamp: receiveTime
        });
      } else if (latency > this.LATENCY_WARNING_THRESHOLD) {
        this.performanceMetrics.latencyWarnings++;
        console.warn(`OrchestrationConnection: High latency detected: ${latency}ms (threshold: ${this.LATENCY_WARNING_THRESHOLD}ms)`);
        this.emit('performanceAlert', {
          type: 'latency_warning',
          latency,
          threshold: this.LATENCY_WARNING_THRESHOLD,
          timestamp: receiveTime
        });
      }
    }
    
    // Update connection uptime
    if (this.performanceMetrics.connectionStartTime > 0) {
      this.performanceMetrics.connectionUptime = receiveTime - this.performanceMetrics.connectionStartTime;
    }
  }

  private addToBatch(data: {type?: string; payload?: unknown; timestamp?: number}): void {
    this.updateBatch.push(data);
    
    // Process batch immediately if it reaches max size
    if (this.updateBatch.length >= this.MAX_BATCH_SIZE) {
      this.processBatch();
      return;
    }
    
    // Set up delayed batch processing if not already scheduled
    if (!this.batchTimeout) {
      this.batchTimeout = setTimeout(() => {
        this.processBatch();
      }, this.BATCH_DELAY);
    }
  }

  private processBatch(): void {
    if (this.updateBatch.length === 0) return;
    
    const batch = [...this.updateBatch];
    this.updateBatch = [];
    
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }
    
    // Process all batched messages
    batch.forEach(data => this.handleMessage(data));
    
    // Emit batch processing complete event for performance monitoring
    this.emit('batchProcessed', { 
      count: batch.length, 
      processingTime: Date.now() - (batch[0]?.timestamp || Date.now())
    });
  }

  public getPerformanceMetrics() {
    return {
      ...this.performanceMetrics,
      isConnected: this.isConnected,
      connectionState: this.connectionState,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  public enableHighFrequencyMode(): void {
    this.send({
      type: 'configure_updates',
      config: {
        highFrequency: true,
        batchUpdates: true,
        maxBatchSize: this.MAX_BATCH_SIZE,
        batchDelay: this.BATCH_DELAY
      }
    });
  }

  public subscribeToAgentBatch(agentIds: string[]): void {
    this.send({
      type: 'subscribe_agent_batch',
      agentIds
    });
  }

  subscribeToAgent(agentId: string): void {
    this.send({
      type: 'subscribe_agent',
      agentId
    });
  }

  subscribeToWorkflow(workflowId: string): void {
    this.send({
      type: 'subscribe_workflow',
      workflowId
    });
  }

  async executeAgentCommand(command: AgentCommand): Promise<CommandResult> {
    return new Promise((resolve, reject) => {
      const commandId = `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Set up one-time listener for response
      const handleResponse = (data: {commandId?: string; result?: CommandResult}) => {
        if (data.commandId === commandId) {
          this.off('commandResult', handleResponse);
          resolve(data.result as CommandResult);
        }
      };

      this.on('commandResult', handleResponse);

      // Send command
      this.send({
        type: 'execute_command',
        commandId,
        ...command
      });

      // Timeout after 30 seconds
      setTimeout(() => {
        this.off('commandResult', handleResponse);
        reject(new Error('Command timeout'));
      }, 30000);
    });
  }

  async executeWorkflow(workflow: WorkflowDefinition): Promise<WorkflowExecution> {
    return new Promise((resolve, reject) => {
      const workflowId = `wf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Set up one-time listener for response
      const handleResponse = (data: {workflowId?: string; execution?: WorkflowExecution}) => {
        if (data.workflowId === workflowId) {
          this.off('workflowStarted', handleResponse);
          resolve(data.execution as WorkflowExecution);
        }
      };

      this.on('workflowStarted', handleResponse);

      // Send workflow
      this.send({
        type: 'start_workflow',
        workflowId,
        ...workflow
      });

      // Timeout after 10 seconds for workflow start
      setTimeout(() => {
        this.off('workflowStarted', handleResponse);
        reject(new Error('Workflow start timeout'));
      }, 10000);
    });
  }

  private send(data: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('OrchestrationConnection: Cannot send message, WebSocket not connected');
    }
  }

  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  private emit(event: string, data: unknown): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`OrchestrationConnection: Error in event handler for ${event}`, error);
        }
      });
    }
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.eventHandlers.clear();
    this.isReconnecting = false;
  }

  /**
   * Validate workflow definition for errors and dependencies
   */
  async validateWorkflow(workflow: WorkflowDefinition): Promise<{valid: boolean; errors?: string[]}> {
    return new Promise((resolve, reject) => {
      const requestId = `validation_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Set up one-time listener for response
      const handleResponse = (data: {requestId?: string; result?: {valid: boolean; errors?: string[]}}) => {
        if (data.requestId === requestId) {
          this.off('workflowValidated', handleResponse);
          resolve(data.result as {valid: boolean; errors?: string[]});
        }
      };

      this.on('workflowValidated', handleResponse);

      // Send validation request
      this.send({
        type: 'validate_workflow',
        requestId,
        workflow
      });

      // Timeout after 5 seconds
      setTimeout(() => {
        this.off('workflowValidated', handleResponse);
        reject(new Error('Workflow validation timeout'));
      }, 5000);
    });
  }

  /**
   * Save workflow definition to backend
   */
  async saveWorkflow(workflow: WorkflowDefinition): Promise<{id: string; saved: boolean}> {
    return new Promise((resolve, reject) => {
      const requestId = `save_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Set up one-time listener for response
      const handleResponse = (data: {requestId?: string; result?: {id: string; saved: boolean}}) => {
        if (data.requestId === requestId) {
          this.off('workflowSaved', handleResponse);
          resolve(data.result as {id: string; saved: boolean});
        }
      };

      this.on('workflowSaved', handleResponse);

      // Send save request
      this.send({
        type: 'save_workflow',
        requestId,
        workflow
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        this.off('workflowSaved', handleResponse);
        reject(new Error('Workflow save timeout'));
      }, 10000);
    });
  }

  /**
   * Load workflow definition from backend
   */
  async loadWorkflow(workflowId: string): Promise<WorkflowDefinition> {
    return new Promise((resolve, reject) => {
      const requestId = `load_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Set up one-time listener for response
      const handleResponse = (data: {requestId?: string; workflow?: WorkflowDefinition}) => {
        if (data.requestId === requestId) {
          this.off('workflowLoaded', handleResponse);
          resolve(data.workflow as WorkflowDefinition);
        }
      };

      this.on('workflowLoaded', handleResponse);

      // Send load request
      this.send({
        type: 'load_workflow',
        requestId,
        workflowId
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        this.off('workflowLoaded', handleResponse);
        reject(new Error('Workflow load timeout'));
      }, 10000);
    });
  }

  /**
   * Get real-time workflow status updates
   */
  onWorkflowProgress(callback: (execution: WorkflowExecution) => void): () => void {
    this.on('workflowProgress', callback);
    return () => this.off('workflowProgress', callback);
  }

  /**
   * Get real-time agent activity updates
   */
  onAgentActivity(callback: (activity: AgentActivity) => void): () => void {
    this.on('agentActivity', callback);
    return () => this.off('agentActivity', callback);
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get connectionState(): string {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN: return 'connected';
      case WebSocket.CLOSING: return 'closing';
      case WebSocket.CLOSED: return 'closed';
      default: return 'unknown';
    }
  }
}

// Export singleton instance
export const OrchestrationConnection = new OrchestrationConnectionClass();
